/**
 * Speichert Kostenbeträge am Nachweis – im Nachweis-Detail auf der Seite "Nachweise".
 *
 * Die Kostenposition ist für den Nutzer unsichtbar: beim ersten eingetragenen Betrag wird sie
 * still angelegt, danach aktualisiert. Ein leerer Betrag auf einer noch nicht existierenden
 * Position legt nichts an, sonst entstünden bei jedem Durchtabben leere Zeilen.
 *
 * Schreibvorgänge desselben Nachweises laufen streng nacheinander: "Geplante Kosten" und
 * "Bisher angefallen" lösen beim Durchtabben zwei Speicherungen kurz hintereinander aus. Ohne
 * die Reihung sähen beide denselben Stand ohne Position und legten je eine an – der Nachweis
 * hätte danach zwei Kostenpositionen statt einer.
 *
 * Beide Felder speichern beim Verlassen, deshalb gilt hier das Blur-Pattern aus
 * docs/adr/004_autosave_dirty_check.md: Dirty-Check vor dem Schreiben, Meldung über
 * `speichern` an PageTemplate.
 */
import { useCallback, useEffect, useRef } from "react";
import { hasAnyFieldChanged } from "../../../../utils/formUtils";
import { SaveState } from "../../../../hooks/useSaveState";

/** Beträge, die eine Kostenposition am Nachweis führt */
export interface EvidenceCostData {
  betrag_geplant?: number | null;
  betrag_ist?: number | null;
}

interface CostItemLike {
  id: string;
  evidence_id?: string;
}

interface UseEvidenceCostSaverProps<T extends CostItemLike> {
  /** Aktueller Stand der bekannten Kostenpositionen */
  costItems: T[];
  createCostItem: (evidenceId: string, data: EvidenceCostData) => Promise<T>;
  /** Bekommt die Position selbst, nicht nur ihre ID: die frisch angelegte steht noch nicht im State */
  updateCostItem: (costItem: T, data: EvidenceCostData) => Promise<T>;
  /** Meldet den Schreibvorgang an PageTemplate; wirft nicht, liefert im Fehlerfall null */
  speichern: SaveState["speichern"];
}

export function useEvidenceCostSaver<T extends CostItemLike>({
  costItems,
  createCostItem,
  updateCostItem,
  speichern,
}: UseEvidenceCostSaverProps<T>) {
  // Über den Ref statt über die Closure gelesen: der Aufruf hängt sich an einen laufenden
  // Schreibvorgang und liefe sonst mit dem Stand von vor dessen Start weiter.
  const costItemsRef = useRef(costItems);
  useEffect(() => {
    costItemsRef.current = costItems;
  }, [costItems]);

  /** Laufender Schreibvorgang je Nachweis; sein Ergebnis ist die Position, auf der weitergearbeitet wird */
  const laufende = useRef(new Map<string, Promise<T | null>>());

  const saveCost = useCallback(
    async (evidenceId: string, data: EvidenceCostData): Promise<void> => {
      const vorheriger = laufende.current.get(evidenceId);
      const lauf = (async (): Promise<T | null> => {
        // Fehlgeschlagene Vorgänger blockieren nicht: dann greift wieder der Blick in die Liste.
        const vorher = vorheriger ? await vorheriger.catch(() => null) : null;
        const vorhanden =
          vorher ??
          costItemsRef.current.find(
            (item) => item.evidence_id === evidenceId,
          ) ??
          null;
        if (vorhanden) {
          // Dirty-Check vor `speichern`: sonst meldete jedes Durchtabben einen Speichervorgang.
          if (!hasAnyFieldChanged(vorhanden, data)) return vorhanden;
          return speichern(() => updateCostItem(vorhanden, data));
        }
        if (!Object.values(data).some((wert) => wert != null)) return null;
        return speichern(() => createCostItem(evidenceId, data));
      })();

      laufende.current.set(evidenceId, lauf);
      try {
        await lauf;
      } finally {
        // Nur den eigenen Eintrag räumen – ein inzwischen gestarteter Nachfolger bleibt stehen.
        if (laufende.current.get(evidenceId) === lauf) {
          laufende.current.delete(evidenceId);
        }
      }
    },
    [createCostItem, updateCostItem, speichern],
  );

  return saveCost;
}
